import type {
  MockCache,
  ChannelId,
  ChannelKind,
  UserId,
  ChannelFolderId,
  Channel,
  ChannelMember,
  MessageId,
  LucideIconName,
  HexColor,
  ChannelFolder,
} from '../../types';
import { dmKey } from '../../helpers';
import { richParagraph } from '../../rich-doc';

import { fanoutLast } from '../fanout';
import { newId } from '../ids';
import { CHANNEL_ICONS, maxSortKey, snapshot, sortKeyBetween } from '../internal';
import { logActivity } from '../journal';
import { insertMessageRow, insertSystemMessage } from './messages';
import { bump, requireCache } from '../runtime';
import { sortKeyAfter, sortKeyLast } from './tree';

function buildChannel(
  cache: MockCache,
  args: {
    id: ChannelId;
    kind: ChannelKind;
    name: string | null;
    memberIds: UserId[];
    topic?: string;
    folderId?: ChannelFolderId | null;
    closed?: boolean;
  },
): Channel {
  const now = cache.nowMs();
  const keys: string[] = [];
  for (const id of cache.channels.keys()) {
    const channel = cache.channels.get(id);
    if (channel !== undefined) {
      keys.push(channel.sortKey);
    }
  }
  const channel: Channel = {
    id: args.id,
    workspaceId: cache.activeWorkspaceId,
    kind: args.kind,
    folderId: args.folderId ?? null,
    closed: args.closed ?? (args.kind === 'private' || args.kind === 'dm' || args.kind === 'group'),
    name: args.name,
    icon: CHANNEL_ICONS[args.kind],
    topic: args.topic ?? '',
    dmKey: args.kind === 'dm' || args.kind === 'group' ? dmKey(args.memberIds) : null,
    creatorId: cache.me,
    nextSeq: 1,
    lastMessageAt: null,
    lastMessageSeq: null,
    sortKey: sortKeyBetween(maxSortKey(keys), null),
    archivedAt: null,
    createdAt: now,
  };
  cache.channels.set(channel.id, channel);
  args.memberIds.forEach((userId, position) => {
    const member: ChannelMember = {
      channelId: channel.id,
      userId,
      role: userId === cache.me || (position === 0 && !args.memberIds.includes(cache.me)) ? 'owner' : 'member',
      lastReadSeq: 0,
      unreadCount: 0,
      mentionCount: 0,
      muted: false,
      joinedAt: now,
    };
    cache.members.set(`${String(channel.id)}:${String(userId)}`, member);
  });
  const joinText = args.name !== null ? `Канал «${args.name}» создан` : 'Личка создана';
  const join = insertMessageRow(cache, {
    id: newId('message') as MessageId,
    channelId: channel.id,
    threadRootId: null,
    replyToId: null,
    bodyJson: richParagraph(joinText),
    sendAt: null,
    systemKind: 'join',
    authorId: cache.me,
  });
  for (const userId of args.memberIds) {
    const member = cache.members.get(`${String(channel.id)}:${String(userId)}`);
    if (member !== undefined) {
      member.lastReadSeq = join.seq;
    }
  }
  logActivity(cache, {
    mutator: 'channel.create',
    entityTable: 'channel',
    entityId: String(channel.id),
    op: 'insert',
    before: null,
    after: snapshot(channel),
  });
  return channel;
}

export function createChannelFolder(args: {
  id: ChannelFolderId;
  name: string;
  icon?: LucideIconName;
  color?: HexColor;
}): ChannelFolder {
  const cache = requireCache();
  const name = args.name.trim();
  if (name.length === 0) {
    throw new Error('Имя папки не может быть пустым');
  }
  const siblings: string[] = [];
  for (const folder of cache.channelFolders.values()) {
    if (folder.archivedAt === null) siblings.push(folder.sortKey);
  }
  const folder: ChannelFolder = {
    id: args.id,
    workspaceId: cache.activeWorkspaceId,
    name,
    icon: args.icon ?? ('Folder' as LucideIconName),
    color: args.color ?? ('var(--identity-1)' as HexColor),
    sortKey: sortKeyBetween(maxSortKey(siblings), null),
    archivedAt: null,
    createdAt: cache.nowMs(),
  };
  cache.channelFolders.set(folder.id, folder);
  bump('channels', 'workspace');
  return folder;
}

export function deleteChannelFolder(args: { id: ChannelFolderId }): void {
  const cache = requireCache();
  const folder = cache.channelFolders.get(args.id);
  if (folder === undefined) {
    throw new Error('Папка не найдена');
  }
  for (const channel of cache.channels.values()) {
    if (channel.folderId === args.id) {
      channel.folderId = null;
    }
  }
  cache.channelFolders.delete(args.id);
  bump('channels', 'workspace');
}

export function updateChannelFolder(args: {
  id: ChannelFolderId;
  name?: string;
  icon?: LucideIconName;
  color?: HexColor;
}): void {
  const cache = requireCache();
  const folder = cache.channelFolders.get(args.id);
  if (folder === undefined) {
    throw new Error('Папка не найдена');
  }
  if (args.name !== undefined) {
    const next = args.name.trim();
    if (next.length === 0) {
      throw new Error('Имя папки не может быть пустым');
    }
    folder.name = next;
  }
  if (args.icon !== undefined) folder.icon = args.icon;
  if (args.color !== undefined) folder.color = args.color;
  bump('channels', 'workspace');
}

export function moveChannelToFolder(args: {
  channelId: ChannelId;
  folderId: ChannelFolderId | null;
  afterId?: ChannelId | null;
}): void {
  const cache = requireCache();
  const channel = cache.channels.get(args.channelId);
  if (channel === undefined) {
    throw new Error('Канал не найден');
  }
  if (channel.kind === 'dm' || channel.kind === 'group') {
    throw new Error('Личные переписки в папки не кладутся');
  }
  if (args.folderId !== null && !cache.channelFolders.has(args.folderId)) {
    throw new Error('Папка не найдена');
  }
  if (channel.folderId === args.folderId && args.afterId === undefined) return;
  const siblings = [...cache.channels.values()].filter(
    (other) =>
      other.workspaceId === channel.workspaceId &&
      other.folderId === args.folderId &&
      other.archivedAt === null &&
      other.kind !== 'dm' &&
      other.kind !== 'group' &&
      other.id !== channel.id,
  );
  const sortKey = args.afterId === undefined ? sortKeyLast(siblings) : sortKeyAfter(siblings, args.afterId);
  const before = { folderId: channel.folderId, sortKey: channel.sortKey };
  channel.folderId = args.folderId;
  channel.sortKey = sortKey;
  logActivity(cache, {
    mutator: 'channel.move',
    entityTable: 'channel',
    entityId: String(channel.id),
    op: 'update',
    before,
    after: { folderId: args.folderId, sortKey },
    taskIds: [],
  });
  fanoutLast(cache);
  bump('channels', 'workspace', `channel:${String(channel.id)}`);
}

export function createChannel(args: {
  id: ChannelId;
  kind: ChannelKind;
  name: string | null;
  memberIds: UserId[];
  topic?: string;
  folderId?: ChannelFolderId | null;
  closed?: boolean;
}): Channel {
  const cache = requireCache();
  const channel = buildChannel(cache, args);
  fanoutLast(cache);
  bump(`channel:${String(channel.id)}`);
  return channel;
}

export function openDirect(args: { id: ChannelId; userIds: UserId[] }): ChannelId {
  const cache = requireCache();
  const key = dmKey(args.userIds);
  for (const channel of cache.channels.values()) {
    if (channel.kind === 'dm' && channel.dmKey === key) {
      return channel.id;
    }
  }
  const channel = buildChannel(cache, { id: args.id, kind: 'dm', name: null, memberIds: args.userIds });
  fanoutLast(cache);
  bump(`channel:${String(channel.id)}`);
  return channel.id;
}

export function joinChannel(args: { channelId: ChannelId; userId?: UserId }): void {
  const cache = requireCache();
  const channel = cache.channels.get(args.channelId);
  if (channel === undefined) {
    throw new Error('Канал не найден');
  }
  const userId = args.userId ?? cache.me;
  const key = `${String(channel.id)}:${String(userId)}`;
  if (cache.members.has(key)) {
    return;
  }
  const now = cache.nowMs();
  const member: ChannelMember = {
    channelId: channel.id,
    userId,
    role: 'member',
    lastReadSeq: channel.lastMessageSeq ?? 0,
    unreadCount: 0,
    mentionCount: 0,
    muted: false,
    joinedAt: now,
  };
  cache.members.set(key, member);
  const user = cache.users.get(userId);
  insertSystemMessage(cache, channel.id, 'join', `${user?.displayName ?? 'Участник'} присоединился к каналу`);
  logActivity(cache, {
    mutator: 'channel.join',
    entityTable: 'channelMember',
    entityId: key,
    op: 'insert',
    before: null,
    after: snapshot(member),
  });
  fanoutLast(cache);
  bump(`channel:${String(channel.id)}`);
}

export function leaveChannel(args: { channelId: ChannelId; userId?: UserId }): void {
  const cache = requireCache();
  const userId = args.userId ?? cache.me;
  const key = `${String(args.channelId)}:${String(userId)}`;
  const member = cache.members.get(key);
  if (member === undefined) {
    return;
  }
  cache.members.delete(key);
  const user = cache.users.get(userId);
  insertSystemMessage(cache, args.channelId, 'leave', `${user?.displayName ?? 'Участник'} покинул канал`);
  logActivity(cache, {
    mutator: 'channel.leave',
    entityTable: 'channelMember',
    entityId: key,
    op: 'delete',
    before: snapshot(member),
    after: null,
  });
  fanoutLast(cache);
  bump(`channel:${String(args.channelId)}`);
}

export function inviteToChannel(args: { channelId: ChannelId; userId: UserId }): void {
  const cache = requireCache();
  const channel = cache.channels.get(args.channelId);
  if (channel === undefined) {
    throw new Error('Канал не найден');
  }
  const key = `${String(channel.id)}:${String(args.userId)}`;
  if (cache.members.has(key)) {
    return;
  }
  const now = cache.nowMs();
  const member: ChannelMember = {
    channelId: channel.id,
    userId: args.userId,
    role: 'member',
    lastReadSeq: channel.lastMessageSeq ?? 0,
    unreadCount: 0,
    mentionCount: 0,
    muted: false,
    joinedAt: now,
  };
  cache.members.set(key, member);
  const user = cache.users.get(args.userId);
  insertSystemMessage(cache, channel.id, 'join', `${user?.displayName ?? 'Участник'} присоединился по приглашению`);
  logActivity(cache, {
    mutator: 'channel.invite',
    entityTable: 'channelMember',
    entityId: key,
    op: 'insert',
    before: null,
    after: snapshot(member),
  });
  fanoutLast(cache);
  bump(`channel:${String(channel.id)}`);
}

export function kickFromChannel(args: { channelId: ChannelId; userId: UserId }): void {
  const cache = requireCache();
  const key = `${String(args.channelId)}:${String(args.userId)}`;
  const member = cache.members.get(key);
  if (member === undefined) {
    return;
  }
  cache.members.delete(key);
  const user = cache.users.get(args.userId);
  insertSystemMessage(cache, args.channelId, 'leave', `${user?.displayName ?? 'Участник'} удалён из канала`);
  logActivity(cache, {
    mutator: 'channel.kick',
    entityTable: 'channelMember',
    entityId: key,
    op: 'delete',
    before: snapshot(member),
    after: null,
  });
  fanoutLast(cache);
  bump(`channel:${String(args.channelId)}`);
}

export function setChannelTopic(args: { channelId: ChannelId; topic: string }): void {
  const cache = requireCache();
  const channel = cache.channels.get(args.channelId);
  if (channel === undefined) {
    throw new Error('Канал не найден');
  }
  const before = { topic: channel.topic };
  channel.topic = args.topic;
  insertSystemMessage(cache, channel.id, 'topic', `Новая тема канала: ${args.topic}`);
  logActivity(cache, {
    mutator: 'channel.setTopic',
    entityTable: 'channel',
    entityId: String(channel.id),
    op: 'update',
    before,
    after: { topic: channel.topic },
  });
  fanoutLast(cache);
  bump(`channel:${String(channel.id)}`);
}

export function setMuted(args: { channelId: ChannelId; muted: boolean }): void {
  const cache = requireCache();
  const key = `${String(args.channelId)}:${String(cache.me)}`;
  const member = cache.members.get(key);
  if (member === undefined) {
    throw new Error('Вы не состоите в канале');
  }
  const before = { muted: member.muted };
  member.muted = args.muted;
  logActivity(cache, {
    mutator: 'channel.setMuted',
    entityTable: 'channelMember',
    entityId: key,
    op: 'update',
    before,
    after: { muted: member.muted },
  });
  fanoutLast(cache);
  bump(`channel:${String(args.channelId)}`);
}

export function archiveChannel(args: { id: ChannelId }): void {
  const cache = requireCache();
  const channel = cache.channels.get(args.id);
  if (channel === undefined) {
    throw new Error('Канал не найден');
  }
  const before = { archivedAt: channel.archivedAt };
  channel.archivedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'channel.archive',
    entityTable: 'channel',
    entityId: String(channel.id),
    op: 'update',
    before,
    after: { archivedAt: channel.archivedAt },
  });
  fanoutLast(cache);
  bump(`channel:${String(channel.id)}`);
}
